/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { DateTime } from 'luxon'

import type { ShipmentRecord } from './types'

/** A start/end pair with a label describing which times they are. */
export interface ShipmentTiming {
  /** Prefix for the row, e.g. 'Expected' or 'Started'. */
  label: string
  start: Date | null
  end: Date | null
}

const toDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Which pair of times actually describes where a shipment is.
 *
 * Showing the planned times for something already under way is misleading, and
 * showing expected times for something finished is just stale — so the pair
 * follows the shipment's progress:
 *   not started  → expected start and end
 *   in progress  → actual start, expected end
 *   ended        → actual start and end
 */
export const shipmentTiming = (shipment: ShipmentRecord): ShipmentTiming => {
  const actualStart = toDate(shipment.actualStartAt)
  const actualEnd = toDate(shipment.actualEndAt)
  const expectedStart = toDate(shipment.expectedStartAt)
  const expectedEnd = toDate(shipment.expectedEndAt)

  // Trust the recorded times over the status: a shipment can carry an actual end
  // before someone gets round to marking it completed.
  if (actualEnd) return { label: 'Ran', start: actualStart ?? expectedStart, end: actualEnd }
  if (actualStart || shipment.status === 'active') {
    return { label: 'Started', start: actualStart ?? expectedStart, end: expectedEnd }
  }
  return { label: 'Expected', start: expectedStart, end: expectedEnd }
}

export const formatTimestamp = (value: Date | null): string =>
  value ? DateTime.fromJSDate(value).toFormat('d LLL yyyy HH:mm') : '—'

/** "Expected 28 Jun 2026 08:00 → 30 Jun 2026 17:00" */
export const formatShipmentTiming = (shipment: ShipmentRecord): string => {
  const { label, start, end } = shipmentTiming(shipment)
  return `${label} ${formatTimestamp(start)} → ${formatTimestamp(end)}`
}

/**
 * The window a shipment actually occupies, for clash detection: whatever is
 * known to have happened, falling back to what is planned.
 */
export const shipmentWindow = (shipment: ShipmentRecord): { start: Date; end: Date } | null => {
  const start = toDate(shipment.actualStartAt) ?? toDate(shipment.expectedStartAt)
  const end = toDate(shipment.actualEndAt) ?? toDate(shipment.expectedEndAt)
  return start && end ? { start, end } : null
}

/**
 * Half-open overlap: two shipments that merely touch (one ends exactly as the
 * next begins) are back-to-back, not a clash.
 */
const windowsOverlap = (a: { start: Date; end: Date }, b: { start: Date; end: Date }): boolean =>
  a.start < b.end && b.start < a.end

/**
 * Shipments whose window overlaps the proposed one. A warning only — a tracker
 * legitimately gets reassigned, and the times entered here are a plan, so this
 * flags a likely mistake rather than refusing the save.
 */
export const conflictingShipments = (
  shipments: ShipmentRecord[],
  start: Date | null,
  end: Date | null,
  ignoreShipmentId?: number
): ShipmentRecord[] => {
  if (!start || !end || end <= start) return []
  const proposed = { start, end }
  return shipments.filter((shipment) => {
    if (shipment.id === ignoreShipmentId) return false
    const window = shipmentWindow(shipment)
    return window !== null && windowsOverlap(window, proposed)
  })
}
